import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ChevronLeft } from 'lucide-react';
import '../components/delivery/delivery.css';
import WizardHeader from '../components/delivery/WizardHeader';
import OrderStep from '../components/delivery/OrderStep';
import ChecklistStep from '../components/delivery/ChecklistStep';
import SummaryStep from '../components/delivery/SummaryStep';
import { api } from '../utils/deliveryApi';

const LAST = 8;

// Wizard "Odvoz a předání": 0 Údaje, 1–7 checklist steps, 8 Shrnutí. Every change is saved immediately.
const DeliveryWizard = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [definition, setDefinition] = useState(null);
  const [protocol, setProtocol] = useState(null);
  const [current, setCurrent] = useState(0);
  const [user, setUser] = useState(null);
  const [saving, setSaving] = useState('');
  const [error, setError] = useState('');
  const headerTimer = useRef(null);

  const reload = useCallback(async () => {
    const p = await api(`/${id}`);
    setProtocol(p);
    return p;
  }, [id]);

  // initial load: definition, protocol and the current user together, state set once afterwards
  useEffect(() => {
    let cancelled = false;
    Promise.all([api('/definition'), api(`/${id}`), fetch('/api/auth/me', { credentials: 'include' }).then((r) => r.json())])
      .then(([def, p, me]) => {
        if (cancelled) return;
        setDefinition(def); setProtocol(p); setCurrent(p.current_step || 0); setUser(me.user || me.data);
      })
      .catch((e) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [id]);

  const run = async (fn) => {
    setSaving('Ukládám…'); setError('');
    try { await fn(); setSaving('Uloženo'); } catch (e) { setError(e.message); setSaving(''); }
  };

  const go = (step) => {
    const next = Math.max(0, Math.min(LAST, step));
    setCurrent(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (protocol?.status === 'draft') api(`/${id}`, { method: 'PATCH', body: { current_step: next } }).catch(() => {});
  };

  // Header fields: optimistic + debounced save
  const changeHeader = (patch) => {
    setProtocol((p) => ({ ...p, header: { ...p.header, ...patch } }));
    clearTimeout(headerTimer.current);
    setSaving('Neuloženo…');
    headerTimer.current = setTimeout(() => run(() => api(`/${id}`, { method: 'PATCH', body: { header: patch } })), 600);
  };
  useEffect(() => () => clearTimeout(headerTimer.current), []);

  const changeItem = (key, patch) => {
    setProtocol((p) => ({ ...p, items: { ...p.items, [key]: { ...(p.items[key] || {}), ...patch } } }));
    run(async () => { await api(`/${id}/items/${key}`, { method: 'PUT', body: patch }); });
  };
  const changeStep = (step, patch) => {
    setProtocol((p) => ({ ...p, steps: { ...p.steps, [step]: { ...(p.steps[step] || {}), ...patch } } }));
    run(async () => { await api(`/${id}/steps/${step}`, { method: 'PUT', body: patch }); });
  };
  const changeNotes = (notes) => {
    setProtocol((p) => ({ ...p, notes }));
    run(() => api(`/${id}`, { method: 'PATCH', body: { notes } }));
  };

  if (error && !protocol) return <div className="dlv" style={{ padding: 24 }}><p role="alert">Protokol se nepodařilo načíst: {error}</p></div>;
  if (!protocol || !definition) return <div className="dlv" style={{ padding: 24 }}>Načítám protokol…</div>;

  const disabled = protocol.status !== 'draft';
  const step = definition.steps.find((s) => s.step === current);
  const title = current === 0 ? 'Údaje o zakázce' : current === LAST ? 'Shrnutí' : step?.title;

  return (
    <div className="dlv" style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <button type="button" className="dlv-iconbtn" style={{ marginBottom: 12 }} onClick={() => navigate('/odvozy')}><ChevronLeft size={16} /> Všechny odvozy</button>
      <WizardHeader protocol={protocol} definition={definition} current={current} onGo={go} />

      {current === 0 && <OrderStep header={protocol.header} onChange={changeHeader} disabled={disabled} />}
      {step && <ChecklistStep key={step.step} step={step} protocol={protocol} onItem={changeItem} onStep={changeStep} onNotes={changeNotes} reload={reload} disabled={disabled} />}
      {current === LAST && <SummaryStep protocol={protocol} user={user} onGo={go} reload={reload} />}

      <div className="dlv-nav" role="navigation" aria-label={`Navigace – ${title}`}>
        <span className="dlv-save" aria-live="polite">{error ? <span style={{ color: '#b71c1c' }}>{error}</span> : disabled ? 'Protokol je uzavřený – jen pro čtení' : saving}</span>
        <button type="button" className="dlv-btn" disabled={current === 0} onClick={() => go(current - 1)}><ArrowLeft size={18} /> Zpět</button>
        {current < LAST && <button type="button" className="dlv-btn dlv-btn--dark" onClick={() => go(current + 1)}>Další <ArrowRight size={18} /></button>}
      </div>
    </div>
  );
};

export default DeliveryWizard;
